import { useRef, useState, type FormEvent } from "react";
import { Droplets, LogOut } from "lucide-react";
import { Spinner } from "../board/Spinner";
import { api, ApiError } from "../api";
import { newId } from "../uuid";

export type OnboardingBusiness = { id: string; name: string };

type Props = {
  // The business that already exists for this signed-in session, straight from the
  // REFRESHED /auth/staff/me — null means nothing exists yet. This is the only thing that
  // decides which step renders, deliberately not a local "did step one just succeed" flag:
  // a reload (or a fresh mount at /onboarding) after the business was created but the site
  // call failed must resume at the site step, never re-run business creation and risk a
  // second business for the same owner.
  business: OnboardingBusiness | null;
  // Only meaningful once `business` is set — they are what lets the wizard choose SiteStep vs
  // ServiceStep for the same non-null business (business missing → BusinessStep regardless of
  // either flag). A site is required before a service can be added, so hasSite is checked first.
  hasSite: boolean;
  hasServices: boolean;
  // A step finished. The caller re-fetches the session — the NEXT render's `business` prop
  // is what actually advances this component, not anything tracked in here.
  onCreated: () => void;
  onSignOut?: () => Promise<void>;
  // Handed back in by ScopeGate after a business-create attempt that failed: the refreshed
  // session (triggered by the same onCreated() call below) still reports no membership, so
  // this component remounts with a fresh BusinessStep. Without these, that remount is a blank
  // form with no explanation — the owner is left retyping into something that silently
  // swallowed their attempt. ScopeGate discards its own copy the moment the resolve finds a
  // business, so a genuinely NEW mount (different session, or after success) never sees stale
  // values here.
  initialError?: string | null;
  initialName?: string;
  // The idempotency key the FAILED attempt used. Reusing it on the retry is what lets the
  // server recognise a resend of the same command — a fresh id would look like a second
  // business and defeat the guard. Undefined on a genuinely new mount, where a new id is right.
  initialClientCommandId?: string;
  // Fired synchronously from BusinessStep's catch, BEFORE onCreated() re-resolves the session.
  // ScopeGate stashes {message, name, clientCommandId} outside its `state` union so the
  // loading→onboarding round-trip that onCreated() triggers doesn't wipe it — that stash is what
  // initialError / initialName / initialClientCommandId above read back on the remount.
  onFailedCreate?: (message: string, name: string, clientCommandId: string) => void;
};

export function OnboardingWizard({
  business, hasSite, hasServices, onCreated, onSignOut,
  initialError, initialName, initialClientCommandId, onFailedCreate,
}: Props) {
  // business missing → BusinessStep; site missing → SiteStep; services missing → ServiceStep.
  // Derived purely from server-sourced props, same discipline as the rest of the wizard: a
  // reload resumes at whichever of these is still incomplete, never at a locally-tracked step.
  // hasSite is checked before hasServices — a site is required before a service can be added.
  let step: "business" | "site" | "service" | "complete" = "business";
  if (business && !hasSite) step = "site";
  else if (business && hasSite && !hasServices) step = "service";
  else if (business && hasSite && hasServices) step = "complete";

  // ScopeGate never renders this component once business+site+services are all present — that
  // combination means onboarding is done and the caller should be showing the board instead.
  // Falling through to BusinessStep here would silently re-run business creation for someone
  // who already has one; fail loudly instead if that invariant is ever violated.
  if (step === "complete") {
    throw new Error("OnboardingWizard rendered with onboarding already complete — this is a bug in the caller.");
  }
  const stepNumber = step === "business" ? 1 : step === "site" ? 2 : 3;
  const title = step === "business"
    ? "Set up your carwash"
    : step === "site"
      ? "Add your first site"
      : "Add your first service";

  return (
    <div className="min-h-screen grid place-items-center p-6" style={{ background: "#eef2f4" }}>
      <div className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-lg">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: "#0fa3b1" }}>
            <Droplets size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">{title}</h1>
            <p className="text-xs text-slate-500">Step {stepNumber} of 3</p>
          </div>
        </div>

        {step === "business" && (
          <BusinessStep
            onCreated={onCreated}
            initialError={initialError}
            initialName={initialName}
            initialClientCommandId={initialClientCommandId}
            onFailedCreate={onFailedCreate}
          />
        )}
        {step === "site" && business && <SiteStep business={business} onCreated={onCreated} />}
        {step === "service" && business && <ServiceStep business={business} onCreated={onCreated} />}

        {onSignOut && (
          <button
            onClick={() => void onSignOut()}
            className="mx-auto flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <LogOut size={16} /> Sign out
          </button>
        )}
      </div>
    </div>
  );
}

function BusinessStep({ onCreated, initialError, initialName, initialClientCommandId, onFailedCreate }: {
  onCreated: () => void;
  initialError?: string | null;
  initialName?: string;
  initialClientCommandId?: string;
  onFailedCreate?: (message: string, name: string, clientCommandId: string) => void;
}) {
  const [name, setName] = useState(initialName ?? "");
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [pending, setPending] = useState(false);
  // Minted once per attempt-chain, never per render or per click: a resubmit after a failure
  // must resend the SAME command so the server can recognise it as a replay. Inherited from the
  // failed attempt when this is a post-failure remount; a fresh id otherwise.
  const commandId = useRef(initialClientCommandId ?? newId());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.createBusiness({
        name: name.trim(), profileId: "carwash", clientCommandId: commandId.current,
      });
      onCreated();
    } catch (err) {
      const message = err instanceof ApiError ? err.detail : "Couldn't create that business. Try again.";
      setError(message);
      setPending(false);
      // Hand the error and the typed name up to ScopeGate BEFORE onCreated() below, so they
      // survive the loading→onboarding round-trip that call triggers (see onFailedCreate's
      // doc comment in OnboardingWizard's Props).
      onFailedCreate?.(message, name.trim(), commandId.current);
      // AMBIGUOUS failure: a response dropped by load shedding is indistinguishable from a real
      // failure, and the business may already have committed server-side. Two things cover it
      // now. Belt: the command carries a clientCommandId, so a blind re-post is recognised as a
      // replay and resolves to the business the first attempt created rather than minting a
      // second Marten tenant. Braces: re-fetching the session (via onCreated, same as a success)
      // means a retry re-derives the step from the SERVER instead of re-posting at all. If the
      // create genuinely failed, the refreshed session still has no membership: this same form
      // reappears, carrying the error, typed name, and original command id handed up above.
      onCreated();
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600">What should we call your carwash?</p>

      <label className="block text-sm font-medium text-slate-700">
        Carwash name
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          required
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
        />
      </label>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      {/* src/apps/CLAUDE.md async-feedback rule: disabled + inline spinner while in flight, with
          the label swapped for the spinner so the button does not resize. */}
      <button
        type="submit" disabled={pending}
        className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white shadow disabled:opacity-60"
        style={{ background: "#0fa3b1" }}
      >
        {pending ? <Spinner /> : "Continue"}
      </button>
    </form>
  );
}

function SiteStep({ business, onCreated }: { business: OnboardingBusiness; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Per attempt-chain, not per click. This step stays mounted on failure, so a ref is enough —
  // no stash back through ScopeGate is needed the way BusinessStep's remount requires.
  const commandId = useRef(newId());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      // No coordinates here (per Task 3) — a location can be added later. Hours are also left
      // for the owner to fill in afterwards; nothing here is required to start taking walk-ins.
      await api.addSite(business.id, {
        name: name.trim(),
        address: address.trim() || null, hours: [],
        clientCommandId: commandId.current,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't add that site. Try again.");
      setPending(false); // stays mounted on failure — the business is already created, don't lose that
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600">{business.name} is set up. Add its first site to start taking walk-ins.</p>

      <label className="block text-sm font-medium text-slate-700">
        Site name
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          required
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
        />
      </label>

      <label className="block text-sm font-medium text-slate-700">
        Address <span className="font-normal text-slate-400">(optional)</span>
        <input
          type="text" value={address} onChange={(e) => setAddress(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
        />
      </label>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      {/* No longer the last step — the wizard's service step follows. */}
      <button
        type="submit" disabled={pending}
        className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white shadow disabled:opacity-60"
        style={{ background: "#0fa3b1" }}
      >
        {pending ? <Spinner /> : "Continue"}
      </button>
    </form>
  );
}

function ServiceStep({ business, onCreated }: { business: OnboardingBusiness; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [prices, setPrices] = useState({ small: "", medium: "", large: "", extraLarge: "" });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Per attempt-chain, not per click — a retry must resend the same command.
  const commandId = useRef(newId());
  const serviceTypeId = useRef(newId());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Give the service a name."); return; }
    setError(null);
    setPending(true);
    try {
      await api.upsertCatalogItem(business.id, serviceTypeId.current, {
        name: name.trim(), active: true,
        prices: {
          Small: Number(prices.small) || 0, Medium: Number(prices.medium) || 0,
          Large: Number(prices.large) || 0, ExtraLarge: Number(prices.extraLarge) || 0,
        },
        clientCommandId: commandId.current,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't add that service. Try again.");
      setPending(false); // stays mounted — the business and site are already created
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600">
        Add your first service so you can start taking walk-ins. You can add more later in Settings.
      </p>

      <label className="block text-sm font-medium text-slate-700">
        Service name
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)} required
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
        />
      </label>

      <fieldset className="grid grid-cols-2 gap-3">
        <legend className="text-sm font-medium text-slate-700 mb-1">Price by vehicle size (R)</legend>
        {([["small", "Small"], ["medium", "Medium"], ["large", "Large"], ["extraLarge", "Extra large"]] as const)
          .map(([key, label]) => (
            <label key={key} className="block text-xs font-medium text-slate-600">
              {label}
              {/* Required: Number(x) || 0 would otherwise let a skipped size silently persist as
                  a real, active R0 price — the backend only rejects NEGATIVE prices, so nothing
                  downstream would catch a missed size. Blocking submission here is what prevents
                  that silent revenue leak. */}
              <input
                type="number" inputMode="numeric" min="0" required
                value={prices[key]}
                onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
              />
            </label>
          ))}
      </fieldset>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      <button
        type="submit" disabled={pending}
        className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white shadow disabled:opacity-60"
        style={{ background: "#0fa3b1" }}
      >
        {pending ? <Spinner /> : "Finish setup"}
      </button>
    </form>
  );
}
